/**
 * Valid field types in our validation system
 */
const VALID_FIELD_TYPES = ["string", "number", "boolean", "array", "object"];

/**
 * Valid schema properties for each field type
 */
const VALID_SCHEMA_PROPERTIES = {
  // Common properties for all types
  common: ["type", "required", "default", "validate"],

  // Type-specific properties
  string: ["minLength", "maxLength", "pattern", "patternMessage"],
  number: ["min", "max"],
  boolean: [],
  array: ["minItems", "maxItems", "itemSchema"],
  object: ["properties"],
};

/**
 * Required properties for each field schema
 */
const REQUIRED_SCHEMA_PROPERTIES = ["type"];

/**
 * Validates a single field schema
 * @param {Object} fieldSchema - Schema for a single field
 * @param {string} fieldName - Name of the field (for error messages)
 * @returns {Array} Array of error messages, empty if valid
 */
export const validateFieldSchema = (fieldSchema, fieldName) => {
  const errors = [];

  // Must be an object
  if (!fieldSchema || typeof fieldSchema !== "object" || Array.isArray(fieldSchema)) {
    return [`Schema for '${fieldName}' must be an object`];
  }

  // Check required properties
  for (const prop of REQUIRED_SCHEMA_PROPERTIES) {
    if (fieldSchema[prop] === undefined) {
      errors.push(`Schema for '${fieldName}' is missing required property '${prop}'`);
    }
  }

  // Check field type
  if (fieldSchema.type !== undefined) {
    if (!VALID_FIELD_TYPES.includes(fieldSchema.type)) {
      errors.push(`Invalid type '${fieldSchema.type}' for '${fieldName}'. Must be one of: ${VALID_FIELD_TYPES.join(", ")}`);
    }

    // Check type-specific properties
    const validProps = [...VALID_SCHEMA_PROPERTIES.common, ...(VALID_SCHEMA_PROPERTIES[fieldSchema.type] || [])];

    // Check for invalid properties
    Object.keys(fieldSchema).forEach((prop) => {
      if (!validProps.includes(prop)) {
        errors.push(`Invalid property '${prop}' for '${fieldName}' of type '${fieldSchema.type}'`);
      }
    });

    // Type-specific validations
    if (fieldSchema.type === "array" && fieldSchema.itemSchema) {
      // Validate item schema recursively
      const itemSchemaErrors = validateSchema(fieldSchema.itemSchema, `${fieldName} items`);
      errors.push(...itemSchemaErrors);
    }

    if (fieldSchema.type === "object" && fieldSchema.properties) {
      // Validate nested properties recursively
      const propertiesErrors = validateSchema(fieldSchema.properties, `${fieldName} properties`);
      errors.push(...propertiesErrors);
    }
  }

  // Validate 'required' is boolean if present
  if (fieldSchema.required !== undefined && typeof fieldSchema.required !== "boolean") {
    errors.push(`'required' property for '${fieldName}' must be a boolean`);
  }

  // Validate 'validate' is a function if present
  if (fieldSchema.validate !== undefined && typeof fieldSchema.validate !== "function") {
    errors.push(`'validate' property for '${fieldName}' must be a function`);
  }

  // Type-specific validations
  if (fieldSchema.type === "string") {
    // Validate string-specific properties
    if (fieldSchema.minLength !== undefined && (typeof fieldSchema.minLength !== "number" || fieldSchema.minLength < 0)) {
      errors.push(`'minLength' for '${fieldName}' must be a non-negative number`);
    }

    if (fieldSchema.maxLength !== undefined && (typeof fieldSchema.maxLength !== "number" || fieldSchema.maxLength < 0)) {
      errors.push(`'maxLength' for '${fieldName}' must be a non-negative number`);
    }

    if (fieldSchema.minLength !== undefined && fieldSchema.maxLength !== undefined && fieldSchema.minLength > fieldSchema.maxLength) {
      errors.push(`'minLength' (${fieldSchema.minLength}) cannot be greater than 'maxLength' (${fieldSchema.maxLength}) for '${fieldName}'`);
    }

    if (fieldSchema.pattern !== undefined && typeof fieldSchema.pattern !== "string") {
      errors.push(`'pattern' for '${fieldName}' must be a string`);
    }
  }

  if (fieldSchema.type === "number") {
    // Validate number-specific properties
    if (fieldSchema.min !== undefined && typeof fieldSchema.min !== "number") {
      errors.push(`'min' for '${fieldName}' must be a number`);
    }

    if (fieldSchema.max !== undefined && typeof fieldSchema.max !== "number") {
      errors.push(`'max' for '${fieldName}' must be a number`);
    }

    if (fieldSchema.min !== undefined && fieldSchema.max !== undefined && fieldSchema.min > fieldSchema.max) {
      errors.push(`'min' (${fieldSchema.min}) cannot be greater than 'max' (${fieldSchema.max}) for '${fieldName}'`);
    }
  }

  if (fieldSchema.type === "array") {
    // Validate array-specific properties
    if (fieldSchema.minItems !== undefined && (typeof fieldSchema.minItems !== "number" || fieldSchema.minItems < 0)) {
      errors.push(`'minItems' for '${fieldName}' must be a non-negative number`);
    }

    if (fieldSchema.maxItems !== undefined && (typeof fieldSchema.maxItems !== "number" || fieldSchema.maxItems < 0)) {
      errors.push(`'maxItems' for '${fieldName}' must be a non-negative number`);
    }

    if (fieldSchema.minItems !== undefined && fieldSchema.maxItems !== undefined && fieldSchema.minItems > fieldSchema.maxItems) {
      errors.push(`'minItems' (${fieldSchema.minItems}) cannot be greater than 'maxItems' (${fieldSchema.maxItems}) for '${fieldName}'`);
    }
  }

  return errors;
};

/**
 * Validates an entire schema
 * @param {Object} schema - Full schema object with field definitions
 * @param {string} schemaName - Name of the schema (for error messages)
 * @returns {Array} Array of error messages, empty if valid
 */
export const validateSchema = (schema, schemaName = "root") => {
  const errors = [];

  // Must be an object
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
    return [`Schema '${schemaName}' must be an object`];
  }

  // Validate each field in the schema
  Object.entries(schema).forEach(([fieldName, fieldSchema]) => {
    // Skip fields starting with underscore (meta fields)
    if (fieldName.startsWith("_")) return;

    const fieldErrors = validateFieldSchema(fieldSchema, fieldName);
    errors.push(...fieldErrors);
  });

  return errors;
};

/**
 * Validates a schema and throws if invalid
 * @param {Object} schema - The schema to validate
 * @param {string} schemaName - Name for error reporting
 * @returns {Object} The original schema (if valid)
 * @throws {Error} If the schema is invalid
 */
export function validateAndUseSchema(schema, schemaName) {
  const errors = validateSchema(schema, schemaName);
  
  if (errors.length > 0) {
    const errorMessage = `Invalid schema: ${schemaName}\n${errors.join('\n')}`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
  
  return schema;
}

const SchemaValidator = {
  validateFieldSchema,
  validateSchema,
  validateAndUseSchema,
};

export default SchemaValidator;
